import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useShop } from "../context/ShopContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { clearCart } = useShop();
  const [status, setStatus] = useState("checking");
  const [detail, setDetail] = useState(null);
  const polls = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setDetail(data);
          setStatus("paid");
          if (!cleared.current) { clearCart(); cleared.current = true; }
          return;
        }
        if (["expired", "failed"].includes(data.status)) {
          setStatus("error");
          return;
        }
      } catch {
        // keep trying
      }
      polls.current += 1;
      if (polls.current > 12) {
        setStatus("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, clearCart]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen pt-40 pb-32 px-6 text-center">
      {status === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="mx-auto animate-spin text-charcoal/40 mb-6" size={40} />
          <h1 className="font-serif text-3xl font-light">Confirming your payment…</h1>
        </div>
      )}
      {status === "paid" && (
        <div className="max-w-lg mx-auto" data-testid="payment-paid">
          <div className="w-16 h-16 mx-auto rounded-full bg-ocean text-sand flex items-center justify-center mb-6">
            <Check size={30} />
          </div>
          <div className="overline text-sunkissed mb-3">Order confirmed</div>
          <h1 className="font-serif text-4xl md:text-5xl font-light">Thank you</h1>
          <p className="text-charcoal/60 mt-4">
            Your made-to-measure pieces are now entering our atelier. You'll find the order in your account.
          </p>
          {detail && (
            <p className="mt-3 font-medium">Total paid: {(detail.currency || "chf").toUpperCase()} {Number(detail.amount).toFixed(2)}</p>
          )}
          <div className="flex flex-wrap justify-center gap-4 mt-9">
            <Link to="/account" data-testid="success-account" className="inline-flex items-center gap-3 bg-ocean text-sand px-8 py-4 overline hover:opacity-90 transition-opacity">
              View my orders <ArrowRight size={16} />
            </Link>
            <Link to="/women" className="inline-flex items-center gap-3 border border-charcoal/25 px-8 py-4 overline hover:border-charcoal transition-colors">
              Keep shopping
            </Link>
          </div>
        </div>
      )}
      {(status === "error" || status === "timeout") && (
        <div className="max-w-lg mx-auto" data-testid="payment-error">
          <h1 className="font-serif text-3xl font-light">We couldn't confirm your payment</h1>
          <p className="text-charcoal/60 mt-3">
            If you were charged, it will still process. Please check your account shortly or contact us.
          </p>
          <Link to="/cart" className="inline-flex items-center gap-3 bg-ocean text-sand px-8 py-4 overline mt-8 hover:opacity-90 transition-opacity">
            Back to bag
          </Link>
        </div>
      )}
    </div>
  );
}
